/**
 * PasswordResetForm Component
 *
 * Complete password reset form component with comprehensive validation,
 * error handling, and seamless authentication integration.
 */

// 1. External dependencies
import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

// 2. Internal absolute imports
import { Button, Input, Form, Alert, Card, CardBody, CardFooter, Link } from '@enfomentis/ui';
import { AppLayout } from '@/components/shared/layouts';
import { useAuth } from '@/hooks/useAuth';
import type { AuthErrorCode } from '@/types/auth';

/**
 * Password Reset Form Component
 *
 * Features:
 * - HeroUI Form and Input components with native validation
 * - Real-time email validation
 * - Server-side error mapping and display
 * - Success state with proper UI feedback
 * - Mobile responsive and accessible design
 */
export function PasswordResetForm(): React.JSX.Element {
  const { requestPasswordReset } = useAuth();

  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);

  // Email validation function
  const validateEmail = (value: string): string | null => {
    if (!value) return "Email is required";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) return "Please enter a valid email address";
    return null;
  };

  // Validation state - show email errors after blur or submit
  const emailError = React.useMemo(() => {
    if (!hasSubmitted && !emailTouched) return null;
    return validateEmail(email);
  }, [email, hasSubmitted, emailTouched]);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setHasSubmitted(true);
    setIsSubmitting(true);
    setServerErrors({});

    try {
      const result = await requestPasswordReset({
        email: email
      });

      if (result.success) {
        setIsSuccess(true);
      } else {
        // Map authentication errors to form field errors
        const errors: Record<string, string> = {};

        switch (result.error as AuthErrorCode) {
          case 'ACCOUNT_NOT_FOUND':
            errors['email'] = 'No account found with this email address';
            break;
          case 'ACCOUNT_DISABLED':
            errors['email'] = 'This account has been disabled';
            break;
          case 'RATE_LIMITED':
            errors['general'] = 'Too many password reset requests. Please try again later';
            break;
          default:
            errors['general'] = result.message || 'Password reset failed. Please try again';
        }

        setServerErrors(errors);
      }
    } catch (error) {
      console.error('Password reset error:', error);
      setServerErrors({ 'general': 'Network error occurred. Please check your connection' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success state - show confirmation message
  if (isSuccess) {
    return (
      <AppLayout hideFooter={true}>
        <Card className="items-center justify-center h-full" radius="none" shadow="none">
          <CardBody className="flex max-w-md mx-auto p-6 mt-10">
            <div className="text-center space-y-6">
              <div className="text-success-600 text-6xl">✓</div>

              <div className="space-y-3">
                <h3 className="text-lg font-medium text-default-900">
                  Password Reset Sent
                </h3>
                <p className="text-default-600">
                  We've sent password reset instructions to <strong>{email}</strong>.
                  Please check your inbox and follow the link to reset your password.
                </p>
              </div>

              <div className="space-y-4">
                <RouterLink to="/login">
                  <Button
                    color="primary"
                    size="lg"
                    className="w-full"
                  >
                    Back to Sign In
                  </Button>
                </RouterLink>

                <Button
                  variant="light"
                  size="sm"
                  onPress={() => {
                    setIsSuccess(false);
                    setEmail('');
                  }}
                  className="w-full"
                >
                  Send to different email
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      </AppLayout>
    );
  }

  // Form state - show email input form
  return (
    <AppLayout hideFooter={true}>
      <Card className="items-center justify-center h-full" radius="none" shadow="none">
        <CardBody className="flex max-w-md mx-auto p-6 mt-10">
          <div className="space-y-6">
            <div className="text-center">
              <h1 className="text-xl font-semibold mb-2">Reset Your Password</h1>
              <p className="text-default-600">
                Enter your email address and we'll send you instructions to reset your password.
              </p>
            </div>

            <Form
              validationBehavior="aria"
              validationErrors={serverErrors}
              onSubmit={handleSubmit}
              className="space-y-6"
            >
              {/* Email Input */}
              <Input
                type="email"
                label="Email"
                placeholder="Enter your email"
                value={email}
                onValueChange={setEmail}
                onBlur={() => setEmailTouched(true)}
                validate={validateEmail}
                isRequired
                isClearable
                autoComplete="email"
                errorMessage={serverErrors['email'] || emailError}
                isInvalid={!!serverErrors['email'] || !!emailError}
                classNames={{
                  input: "text-base", // 16px font size to prevent mobile zoom
                }}
              />

              {/* General Error Display */}
              {serverErrors['general'] && (
                <Alert
                  color="warning"
                  variant="flat"
                  description={serverErrors['general']}
                />
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                color="primary"
                size="lg"
                isLoading={isSubmitting}
                className="w-full"
              >
                {isSubmitting ? 'Sending...' : 'Send Reset Instructions'}
              </Button>
            </Form>
          </div>
        </CardBody>

        {/* Auth Navigation Links */}
        <CardFooter className="max-w-md mx-auto justify-center border-t border-default-100">
          <div className="text-center">
            <p className="text-sm text-text-secondary">
              Remember your password?{' '}
              <Link as={RouterLink} {...({ to: "/login" } as { to: string })} color="primary" size="sm">
                Sign in here
              </Link>
            </p>
            <p className="text-sm text-text-secondary">
              Not a beta tester?{' '}
              <Link as={RouterLink} {...({ to: "/beta-application" } as { to: string })} color="primary" size="sm">
                Apply to be one here
              </Link>
            </p>
          </div>
        </CardFooter>
      </Card>
    </AppLayout>
  );
}